// Bucățile comune ale importurilor din „Întrebări → Unelte” (popup-urile Import bilete
// corectate, Bilet nou din JSON, Import teorie): alegerea fișierului JSON, avertismentele,
// jurnalul aplicării și biletele de examen cu întrebările lor, citite ca în fosta pagină
// Corectare examen.

import React, { useRef } from "react";

import { getExamTickets } from "../../../api/examAdminService";
import {
   normalizeExamQuestionRecord,
   parseJsonArraySafe,
   toPositiveInt,
} from "../../../utils/examQuestionFormat";
import { ButtonUI, ListUI, StatusUI } from "../../UI";

const TICKETS_PAGE_LIMIT = 100;

export function safeList(value) {
   return Array.isArray(value) ? value : [];
}

function isObject(value) {
   return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function uniqPositiveInts(values) {
   return Array.from(
      new Set(
         safeList(values)
            .map((item) => Number(item))
            .filter((item) => Number.isInteger(item) && item > 0),
      ),
   );
}

// ── fișierul ─────────────────────────────────────────────────────────────────

/** Textul JSON al fișierului; `lenient` mai încearcă o dată fără virgulele de la capăt. */
export async function readJsonFile(file, { lenient = false } = {}) {
   const text = String((await file.text()) ?? "").replace(/^﻿/, "");
   try {
      return JSON.parse(text);
   } catch (error) {
      if (!lenient) throw error;
      return JSON.parse(text.replace(/,\s*([}\]])/g, "$1"));
   }
}

/** Butonul care alege un fișier JSON (câmpul de fișier e ascuns). */
export function JsonFileButton({ onFile, disabled, variant = "neutral", children = "Alege fișierul JSON" }) {
   const inputRef = useRef(null);
   return (
      <>
         <input
            ref={inputRef}
            type="file"
            accept="application/json,.json"
            hidden
            onChange={(e) => {
               const file = e.target.files?.[0];
               e.target.value = "";
               if (file) onFile(file);
            }}
         />
         <ButtonUI variant={variant} disabled={disabled} onClick={() => inputRef.current?.click()}>
            {children}
         </ButtonUI>
      </>
   );
}

// ── avertismentele și jurnalul ───────────────────────────────────────────────

export function ImportWarnings({ items, max = 12, title = "Avertismente import" }) {
   const list = safeList(items);
   if (!list.length) return null;
   return (
      <StatusUI look="card" tone="warning" title={title}>
         <ul className="questionsImport__list">
            {list.slice(0, max).map((item, i) => (
               <li key={`${i}-${item}`}>{item}</li>
            ))}
            {list.length > max && <li>… și încă {list.length - max}</li>}
         </ul>
      </StatusUI>
   );
}

const LOG_TONE = { ok: "ok", error: "bad", warning: "warn" };

/** „Jurnal aplicare”: un rând pe bilet sau pe întrebare (ok / warning / error). */
export function ImportLog({ rows, max = 30 }) {
   const list = safeList(rows);
   if (!list.length) return null;
   return (
      <ListUI title="Jurnal aplicare" count={list.length}>
         {list.slice(0, max).map((row, i) => (
            <ListUI.Item
               key={`${row.target}-${i}`}
               compact
               tone={LOG_TONE[row.status]}
               title={row.target}
               detail={row.message}
            />
         ))}
         {list.length > max && <ListUI.Empty>… și încă {list.length - max} rânduri</ListUI.Empty>}
      </ListUI>
   );
}

// ── biletele de examen (ca în Corectare examen) ──────────────────────────────

function readItems(raw, fallbackKeys = []) {
   if (Array.isArray(raw)) return raw;
   if (!isObject(raw)) return [];

   const keys = ["data", "items", "results", "rows", "tickets", "questions", ...fallbackKeys];

   for (const key of keys) {
      const value = raw?.[key];
      if (Array.isArray(value)) return value;
   }

   const nestedData = raw?.data;
   if (isObject(nestedData)) {
      for (const key of keys) {
         const value = nestedData?.[key];
         if (Array.isArray(value)) return value;
      }
   }

   return [];
}

function unwrapDataObject(raw) {
   if (!isObject(raw)) return raw;
   if (isObject(raw?.data) && !Array.isArray(raw?.data)) return raw.data;
   return raw;
}

function normalizeTicketSummary(item, fallbackIndex = 0) {
   if (!isObject(item)) return null;

   const id = toPositiveInt(item?.id);
   if (!id) return null;

   const name = String(item?.name ?? `Bilet ${fallbackIndex + 1}`).trim();
   const type = String(item?.type ?? "EXAM").trim().toUpperCase() || "EXAM";
   const questionCount =
      toPositiveInt(item?._count?.questions ?? item?.questionCount ?? item?.questionsCount) ??
      uniqPositiveInts(item?.questionIds).length;

   return { id, name, type, questionCount };
}

/** Toate biletele de examen (id, nume, câte întrebări), fără paginare, după id. */
export async function loadAllExamTickets() {
   const collected = [];

   for (let page = 1; page <= 500; page += 1) {
      const response = await getExamTickets({ page, limit: TICKETS_PAGE_LIMIT, type: "EXAM" });
      const rows = readItems(response, ["tickets"])
         .map((item, index) => normalizeTicketSummary(item, index))
         .filter(Boolean);
      collected.push(...rows);

      const totalPages = Number(response?.pagination?.totalPages);
      if (Number.isInteger(totalPages) && totalPages > 0 ? page >= totalPages : rows.length < TICKETS_PAGE_LIMIT) break;
   }

   const byId = new Map();
   collected.forEach((row) => {
      if (row?.id && !byId.has(row.id)) byId.set(row.id, row);
   });
   return Array.from(byId.values()).sort((left, right) => Number(left.id) - Number(right.id));
}

/** Întrebările unui bilet de examen (răspunsul de la GET /exams/tickets/:id/questions). */
export function normalizeTicketQuestions(raw) {
   return readItems(raw, ["questions"])
      .map((question, index) => normalizeExamQuestionRecord(question, index))
      .filter(Boolean)
      .sort(
         (left, right) =>
            Number(left?.order ?? 0) - Number(right?.order ?? 0) || Number(left?.id ?? 0) - Number(right?.id ?? 0),
      );
}

function readQuestionIds(raw, fallbackQuestions = []) {
   const source = unwrapDataObject(raw);

   const directIds = uniqPositiveInts(parseJsonArraySafe(source?.questionIds, safeList(source?.questionIds)));
   if (directIds.length) return directIds;

   const nestedIds = uniqPositiveInts(safeList(source?.questions).map((question) => question?.id ?? question?.questionId));
   if (nestedIds.length) return nestedIds;

   return uniqPositiveInts(safeList(fallbackQuestions).map((question) => question?.id));
}

/** Biletul (id, nume, ordinea întrebărilor) din GET /exams/tickets/:id. */
export function normalizeTicketDetail(raw, fallbackSummary, normalizedQuestions) {
   const source = unwrapDataObject(raw);
   const id = toPositiveInt(source?.id ?? fallbackSummary?.id);
   if (!id) return null;

   return {
      id,
      name: String(source?.name ?? fallbackSummary?.name ?? `Bilet ${id}`).trim() || `Bilet ${id}`,
      type: String(source?.type ?? fallbackSummary?.type ?? "EXAM").trim().toUpperCase() || "EXAM",
      questionIds: readQuestionIds(source, normalizedQuestions),
   };
}
